import {Equation} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/FlatEquationSolver';

export enum StoichiometryVariable {
  LIMITING_REACTANT_EQUIVALENCE = 'LIMITING_REACTANT_EQUIVALENCE',
  LIMITING_REACTANT_MOLS = 'LIMITING_REACTANT_MOLS',
  LIMITING_REACTANT_MOL_UNIT_CONVERSION = 'LIMITING_REACTANT_MOL_UNIT_CONVERSION',
  EQUIVALENCE = 'EQUIVALENCE',
  MOLECULAR_WEIGHT = 'MOLECULAR_WEIGHT',
  MOLS = 'MOLS',
  MOL_UNIT_CONVERSION = 'MOL_UNIT_CONVERSION',
  AMOUNT = 'AMOUNT',
  AMOUNT_UNIT_CONVERSION = 'AMOUNT_UNIT_CONVERSION',
}

export const LIMITING_REACTANT_VARIABLES = new Set([
  StoichiometryVariable.LIMITING_REACTANT_EQUIVALENCE,
  StoichiometryVariable.LIMITING_REACTANT_MOLS,
  StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION,
]);

const EQUIVALENCE_FROM_MOLS_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.EQUIVALENCE,
  deps: [StoichiometryVariable.MOLS, StoichiometryVariable.LIMITING_REACTANT_MOLS],
  calc: (args) => {
    const {
      [StoichiometryVariable.MOLS]: mols,
      [StoichiometryVariable.LIMITING_REACTANT_MOLS]: limitingReactantMols,
    } = args;
    return mols / limitingReactantMols;
  },
};

const EQUIVALENCE_FROM_AMOUNT_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.EQUIVALENCE,
  deps: [
    StoichiometryVariable.AMOUNT,
    StoichiometryVariable.MOLECULAR_WEIGHT,
    StoichiometryVariable.LIMITING_REACTANT_MOLS,
  ],
  calc: (args) => {
    const {
      [StoichiometryVariable.AMOUNT]: amount,
      [StoichiometryVariable.MOLECULAR_WEIGHT]: molecularWeight,
      [StoichiometryVariable.LIMITING_REACTANT_MOLS]: limitingReactantMols,
    } = args;
    return amount / (molecularWeight * limitingReactantMols);
  },
};

export const MOLS_FROM_AMOUNT_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.MOLS,
  deps: [StoichiometryVariable.AMOUNT, StoichiometryVariable.MOLECULAR_WEIGHT],
  calc: (args) => {
    const {
      [StoichiometryVariable.AMOUNT]: amount,
      [StoichiometryVariable.MOLECULAR_WEIGHT]: molecularWeight,
    } = args;
    return amount / molecularWeight;
  },
};

const MOLS_FROM_EQUIVALENCE_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.MOLS,
  deps: [StoichiometryVariable.EQUIVALENCE, StoichiometryVariable.LIMITING_REACTANT_MOLS],
  calc: (args) => {
    const {
      [StoichiometryVariable.EQUIVALENCE]: equivalence,
      [StoichiometryVariable.LIMITING_REACTANT_MOLS]: limitingReactantMols,
    } = args;
    return equivalence * limitingReactantMols;
  },
};

const AMOUNT_FROM_MOLS_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.AMOUNT,
  deps: [StoichiometryVariable.MOLS, StoichiometryVariable.MOLECULAR_WEIGHT],
  calc: (args) => {
    const {[StoichiometryVariable.MOLS]: mols, [StoichiometryVariable.MOLECULAR_WEIGHT]: molecularWeight} =
      args;
    return mols * molecularWeight;
  },
};

const AMOUNT_FROM_EQUIVALENCE_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.AMOUNT,
  deps: [
    StoichiometryVariable.EQUIVALENCE,
    StoichiometryVariable.MOLECULAR_WEIGHT,
    StoichiometryVariable.LIMITING_REACTANT_MOLS,
  ],
  calc: (args) => {
    const {
      [StoichiometryVariable.EQUIVALENCE]: equivalence,
      [StoichiometryVariable.MOLECULAR_WEIGHT]: molecularWeight,
      [StoichiometryVariable.LIMITING_REACTANT_MOLS]: limitingReactantMols,
    } = args;
    return equivalence * molecularWeight * limitingReactantMols;
  },
};

export const STOICHIOMETRY_CALCULATION_EQUATIONS: Array<Equation<StoichiometryVariable>> = [
  EQUIVALENCE_FROM_MOLS_EQUATION,
  EQUIVALENCE_FROM_AMOUNT_EQUATION,
  MOLS_FROM_AMOUNT_EQUATION,
  MOLS_FROM_EQUIVALENCE_EQUATION,
  AMOUNT_FROM_MOLS_EQUATION,
  AMOUNT_FROM_EQUIVALENCE_EQUATION,
];
